import React, { useCallback, useContext } from 'react';
import {
  Col,
  Divider,
  Form,
  Input,
  Row,
  Select,
  Checkbox,
  Skeleton,
  FormInstance,
} from 'antd';
import { useTranslation } from 'react-i18next';
import { validateCpfCnpj } from '@utils/validators';
import SessionContext from '@providers/Session/SessionContext';
import rolesConstants from '@utils/rolesConstants';
import { formatCpfCnpj } from '@utils/formatters';

const { Item } = Form;

interface IUserForm {
  form: FormInstance<any>;
  isNewUser: boolean;
  onFormFinish?: (values: any) => void;
  allowRoles?: boolean;
  optionsGroups?: any;
  loading?: boolean;
  userToUpdateId?: number;
  isUserLoggedToUpdate?: boolean;
}

const UserForm = ({
  form,
  isNewUser,
  isUserLoggedToUpdate,
  loading,
  allowRoles,
  optionsGroups,
  userToUpdateId,
  onFormFinish,
}: IUserForm) => {
  const { t } = useTranslation(['users']);
  const { hasPermission } = useContext(SessionContext);

  const isCustomerCanManageGroups = hasPermission(rolesConstants.MANAGE_GROUPS);

  const getCustomerIdsByGroup = useCallback(
    (groupId: string) => {
      const customerIdByGroupId = optionsGroups?.find(
        (opt: any) => opt?.value === groupId,
      )?.customerId;

      return {
        mainId:
          customerIdByGroupId === userToUpdateId
            ? userToUpdateId
            : customerIdByGroupId,
        allIds: [customerIdByGroupId],
      };
    },
    [optionsGroups, userToUpdateId],
  );

  const onFinish = useCallback(
    (values: any) => {
      if (onFormFinish) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { confirm, password, locale, ...restValues } = values;

        restValues.attributes.locale = locale ?? 'pt';

        if (password) {
          restValues.credentials = [{ value: password }];
        }
        if (allowRoles) {
          const { mainId, allIds } = getCustomerIdsByGroup(restValues.groupId);
          restValues.attributes.customerId = mainId;
          restValues.attributes.customerIds = allIds;
        }

        onFormFinish(restValues);
      }
    },
    [allowRoles, getCustomerIdsByGroup, onFormFinish],
  );

  return (
    <Skeleton loading={loading} active>
      <Form
        id="userForm"
        form={form}
        scrollToFirstError
        onFinish={onFinish}
        colon={false}
        layout="vertical"
      >
        <Row gutter={[16, 16]}>
          <Col sm={12} xs={24}>
            {/* First name */}
            <Item
              label={t('modal.form.labels.firstName')}
              name="firstName"
              rules={[
                {
                  required: true,
                  message: t('modal.form.msgs.fillThisField'),
                },
              ]}
            >
              <Input />
            </Item>
          </Col>
          <Col sm={12} xs={24}>
            {/* Last name */}
            <Item
              label={t('modal.form.labels.lastName')}
              name="lastName"
              rules={[
                {
                  required: true,
                  message: t('modal.form.msgs.fillThisField'),
                },
              ]}
            >
              <Input />
            </Item>
          </Col>
        </Row>
        <Row gutter={[16, 16]}>
          <Col sm={12} xs={24}>
            {/* CPF/CNPJ */}
            <Item
              label={t('modal.form.labels.cpfCnpj')}
              name={['attributes', 'cpfCnpj']}
              normalize={value => formatCpfCnpj(value)}
              rules={[
                {
                  validator: (rule, value) => {
                    if (!value || validateCpfCnpj(value)) {
                      return Promise.resolve();
                    }
                    return Promise.reject(t('modal.form.msgs.invalidCpfCnpj'));
                  },
                },
              ]}
            >
              <Input maxLength={18} />
            </Item>
          </Col>
          <Col sm={12} xs={24}>
            {/* Phone number */}
            <Item
              label={t('modal.form.labels.phoneNumber')}
              name={['attributes', 'phoneNumber']}
            >
              <Input />
            </Item>
          </Col>
        </Row>
        <Row gutter={[16, 16]}>
          <Col sm={12} xs={24}>
            {/* Username */}
            <Item
              label={t('modal.form.labels.username')}
              name="username"
              rules={[
                {
                  required: true,
                  message: t('modal.form.msgs.fillThisField'),
                },
                {
                  validator: (rule, value) => {
                    // @ts-ignore
                    const hasWhitespace = value?.indexOf(' ') !== -1;
                    if (value && hasWhitespace) {
                      return Promise.reject(
                        t('modal.form.msgs.removeTheBlanks'),
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input autoComplete="off" disabled={!isNewUser} />
            </Item>
          </Col>
          <Col sm={12} xs={24}>
            {/* Email */}
            <Item
              label={t('modal.form.labels.email')}
              name="email"
              rules={[
                {
                  required: true,
                  message: t('modal.form.msgs.fillThisField'),
                },
                {
                  type: 'email',
                  message: t('modal.form.msgs.notValidEmail'),
                },
              ]}
            >
              <Input disabled={!isNewUser} />
            </Item>
          </Col>
        </Row>
        <Row gutter={[16, 16]}>
          <Col sm={12} xs={24}>
            {/* Locale */}
            <Item label={t('locales.title')} name="locale">
              <Select
                options={[
                  { value: 'pt', label: 'pt' },
                  { value: 'en', label: 'en' },
                  { value: 'es', label: 'es' },
                ]}
              />
            </Item>
          </Col>
        </Row>

        {allowRoles && (
          <>
            <Divider style={{ marginTop: 0 }} />

            <Row gutter={[16, 16]}>
              <Col span={24}>
                {/* Profile */}
                <Item
                  label={t('modal.form.labels.profile')}
                  name="groupId"
                  rules={[
                    {
                      required: true,
                      message: t('modal.form.msgs.fillThisField'),
                    },
                  ]}
                >
                  <Select
                    loading={!optionsGroups}
                    optionLabelProp="label"
                    options={optionsGroups}
                    showSearch
                    disabled={
                      !isCustomerCanManageGroups || isUserLoggedToUpdate
                    }
                    filterOption={(input, option) => {
                      return (
                        String(option?.label).toLowerCase().indexOf(input) >= 0
                      );
                    }}
                  />
                </Item>
              </Col>
            </Row>
          </>
        )}

        <Divider style={{ marginTop: 0 }} />

        <Row gutter={[16, 16]}>
          <Col sm={12} xs={24}>
            {/* Password */}
            <Item
              label={t('modal.form.labels.password')}
              name="password"
              rules={[
                {
                  required: isNewUser,
                  message: t('modal.form.msgs.fillThisField'),
                },
                {
                  min: 10,
                  message: t('modal.form.msgs.passwordLength', {
                    minLength: 10,
                  }),
                },
              ]}
            >
              <Input.Password />
            </Item>
          </Col>
          <Col sm={12} xs={24}>
            {/* Confirm password */}
            <Item
              dependencies={['password']}
              label={t('modal.form.labels.confirmPassword')}
              name="confirm"
              rules={[
                {
                  required: isNewUser,
                  message: t('modal.form.msgs.fillThisField'),
                },
                ({ getFieldValue }) => ({
                  validator(rule, value) {
                    return !value || getFieldValue('password') === value
                      ? Promise.resolve()
                      : Promise.reject(
                          t('modal.form.msgs.passwordDoesNotMatch'),
                        );
                  },
                }),
              ]}
              hasFeedback
            >
              <Input.Password />
            </Item>
          </Col>
        </Row>

        <Divider>
          <span style={{ opacity: 0.6, fontSize: '0.9rem' }}>
            {t('modal.form.labels.onNextLogin')}
          </span>
        </Divider>

        {/* Required actions */}
        <Item name="requiredActions">
          <Checkbox.Group style={{ width: '100%', padding: '0 20px' }}>
            <Row gutter={[16, 16]}>
              <Col sm={12} xs={24}>
                <Checkbox value="VERIFY_EMAIL" style={{ margin: 0 }}>
                  {t('modal.form.labels.verifyEmail')}
                </Checkbox>
              </Col>
              <Col sm={12} xs={24}>
                <Checkbox value="UPDATE_PASSWORD" style={{ margin: 0 }}>
                  {t('modal.form.labels.updatePassword')}
                </Checkbox>
              </Col>
            </Row>
          </Checkbox.Group>
        </Item>
      </Form>
    </Skeleton>
  );
};

export default UserForm;
